import type { Workflow } from '@compass/client/types/workflow';
import { Check } from 'lucide-react';
import React, { useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { isUserVisibleWorkflow } from '@/utils/workflow';

interface ManufacturerGroup {
  programName: string;
  workflows: Workflow[];
}

// Only offer creatable manufacturers: an id + program_id are needed to build
// the create-case route, and the workflow must be user-visible, i.e. not
// internal (mirrors the Header's configured-workflows filter). Grouped by
// program name, both levels sorted alphabetically.
function groupManufacturersByProgram(
  workflows: Workflow[],
  programsById: Map<string, string>,
): ManufacturerGroup[] {
  const byProgram = new Map<string, Workflow[]>();
  for (const workflow of workflows) {
    if (!workflow.id || !workflow.program_id || !isUserVisibleWorkflow(workflow)) continue;
    const programName = programsById.get(workflow.program_id) || workflow.program_id;
    byProgram.set(programName, [...(byProgram.get(programName) ?? []), workflow]);
  }
  return [...byProgram.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([programName, programWorkflows]) => ({
      programName,
      workflows: [...programWorkflows].sort((a, b) =>
        (a.name || a.id || '').localeCompare(b.name || b.id || ''),
      ),
    }));
}

interface ManufacturerPickerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** PQC workflows ("manufacturers") the user can access across the org. */
  workflows: Workflow[];
  /** Program ID → name, for the per-program group headings. */
  programsById: Map<string, string>;
  /** Called with the confirmed manufacturer (PQC workflow); caller handles navigation. */
  onCreate: (workflow: Workflow) => void;
}

/**
 * "Create Case" manufacturer picker for the tier-2 org-wide cases page.
 *
 * The org-wide view has no selected program or workflow, so creating a case
 * first needs a manufacturer (= PQC workflow). Picking one highlights it and
 * arms the confirm CTA; each workflow carries its `program_id`, which resolves
 * the program-scoped create-case route the caller navigates to on confirm.
 */
export const ManufacturerPickerDialog: React.FC<ManufacturerPickerDialogProps> = ({
  open,
  onOpenChange,
  workflows,
  programsById,
  onCreate,
}) => {
  // Two-step flow: click/Enter picks a manufacturer, the footer CTA confirms.
  const [selected, setSelected] = useState<Workflow | null>(null);

  const groups = groupManufacturersByProgram(workflows, programsById);

  // Clear the selection on close so a reopen starts fresh (the search input
  // resets itself — Radix unmounts the dialog content when closed).
  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) setSelected(null);
    onOpenChange(nextOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md p-0 gap-0">
        <DialogHeader className="px-6 pt-6 pb-4">
          <DialogTitle>Create Case</DialogTitle>
          <DialogDescription>Select the manufacturer to create a case for.</DialogDescription>
        </DialogHeader>
        {/* Flush section between header and footer: strip Command's standalone
         * rounding/background. The search input is a contained field — border +
         * radius, inset to the dialog's 24px gutter — rather than a full-width
         * band. The shared CommandInput hardcodes its wrapper (icon + border-b,
         * no wrapper-class prop), so the box styles reach it via the child
         * selector on our padding container; cmdk wires the input through
         * context, so the extra div between Command and its input is fine. */}
        <Command className="rounded-none bg-transparent border-t">
          <div className="px-6 py-3 [&>div]:rounded-md [&>div]:border">
            <CommandInput placeholder="Search manufacturers..." />
          </div>
          <CommandList className="px-3 pb-2 border-b">
            <CommandEmpty>No manufacturers found.</CommandEmpty>
            {groups.map(group => (
              <CommandGroup key={group.programName} heading={group.programName}>
                {group.workflows.map(workflow => (
                  <CommandItem
                    key={workflow.id}
                    // Include the program name so searching matches either level.
                    value={`${workflow.name || workflow.id} ${group.programName}`}
                    onSelect={() => setSelected(workflow)}
                  >
                    <span className="truncate">{workflow.name || workflow.id}</span>
                    <Check
                      className={cn(
                        'ml-auto',
                        selected?.id === workflow.id ? 'opacity-100' : 'opacity-0',
                      )}
                    />
                  </CommandItem>
                ))}
              </CommandGroup>
            ))}
          </CommandList>
        </Command>
        <DialogFooter className="px-6 py-4">
          <Button
            className="min-w-0"
            disabled={!selected}
            onClick={() => selected && onCreate(selected)}
          >
            <span className="truncate">
              {selected ? `Create Case for "${selected.name || selected.id}"` : 'Create Case'}
            </span>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
